import React from 'react';
import ReactDOM from 'react-dom';
import './index.css';
import App from './App';
import { useAppDispatch } from './hooks';
import reportWebVitals from './reportWebVitals';
import { WagmiConfig } from 'wagmi';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { wagmiConfig } from './wagmi';
import account from './state/slices/account';
import application from './state/slices/application';
import logs from './state/slices/logs';
import { BitstraysTokenFactory } from '@bitstrays/sdk';
import { ApolloProvider } from '@apollo/client';
import { clientFactory } from './wrappers/subgraph';
import LogsUpdater from './state/updaters/logs';
import config from './config';
import { WebSocketProvider } from '@ethersproject/providers';
import { ConnectedRouter, connectRouter } from 'connected-react-router';
import { createBrowserHistory, History } from 'history';
import { applyMiddleware, createStore, combineReducers, PreloadedState } from 'redux';
import { routerMiddleware } from 'connected-react-router';
import { Provider } from 'react-redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import sales, { setIsSaleActive, setIsPreSaleActive, setLastSaleId } from './state/slices/sales';

export const history = createBrowserHistory();

const createRootReducer = (history: History) =>
  combineReducers({
    router: connectRouter(history),
    account,
    sales,
    application,
    logs
  });

export default function configureStore(preloadedState: PreloadedState<any>) {
  const store = createStore(
    createRootReducer(history), // root reducer with router state
    preloadedState,
    composeWithDevTools(
      applyMiddleware(
        routerMiddleware(history), // for dispatching history actions
        // ... other middlewares ...
      ),
    ),
  );

  return store;
}

const store = configureStore({});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

const queryClient = new QueryClient();

const client = clientFactory(config.app.subgraphApiUri);

const Updaters = () => {
  return (
    <>
      <LogsUpdater />
    </>
  );
};

const ChainSubscriber: React.FC = () => {
  const dispatch = useAppDispatch();
  const loadState = async () => {
    const wsProvider = new WebSocketProvider(config.app.wsRpcUri);
    const bitstraysTokenContract = BitstraysTokenFactory.connect(
      config.addresses.bitstraysToken,
      wsProvider,
    );
    const isSalesActive = await bitstraysTokenContract.isSaleActive()
    const isPresaleActive = await bitstraysTokenContract.isPresaleActive()
    const lastSaleId = await bitstraysTokenContract.totalSupply()
    dispatch(setIsSaleActive(isSalesActive));
    dispatch(setIsPreSaleActive(isPresaleActive));
    dispatch(setLastSaleId(lastSaleId.toNumber()));
  };
  loadState();

  return <></>;
};

ReactDOM.render(
  <Provider store={store}>
    <ConnectedRouter history={history}>
      <ChainSubscriber />
      <React.StrictMode>
        <QueryClientProvider client={queryClient}>
          <WagmiConfig config={wagmiConfig}>
            <ApolloProvider client={client}>
              <App />
              <Updaters />
            </ApolloProvider>
          </WagmiConfig>
        </QueryClientProvider>
      </React.StrictMode>
    </ConnectedRouter>
  </Provider>,
  document.getElementById('root'),
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
